import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import ProgramsList from '../components/programs/ProgramsList';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePrograms } from '../contexts/ProgramsContext';
import { useUser } from '../contexts/UserContext';
import { useProgramCategories } from '../contexts/ProgramCategoriesContext';
import { activityLogService } from '../services/activityLogService';

const Programs: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { programs, loading, error } = usePrograms();
  const { user } = useUser();
  const { getCategoryById } = useProgramCategories();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryFilter = searchParams.get('category') || '';
  const fromEdvisor = searchParams.get('fromEdvisor');

  // Get category description from context
  const category = categoryFilter ? getCategoryById(categoryFilter) : undefined;
  const categoryDescription = category?.description || '';

  // Log the program category pageview activity when category is available
  useEffect(() => {
    const logPageview = async () => {
      if (categoryFilter && category && user?.uid) {
        try {
          await activityLogService.logProgramCategoryPageview(user.uid, category.name || categoryFilter);
        } catch (error) {
          console.error('Error logging program category pageview activity:', error);
          // Don't block the flow if logging fails
        }
      }
    };

    logPageview();
  }, [categoryFilter, category, user?.uid]);

  const filteredPrograms = categoryFilter
    ? programs.filter((p) => p.category === categoryFilter)
    : programs;

  console.log('🔄 Programs page:', {
    categoryFilter,
    totalPrograms: programs.length,
    filteredProgramsCount: filteredPrograms.length,
    samplePrograms: filteredPrograms.slice(0, 3).map(p => ({ id: p.id, program: p.program, category: p.category }))
  });



  // Map Program[] to ProgramListItem[] for ProgramsList
  const programListItems = filteredPrograms.map((p) => ({
    id: p.id,
    program: p.program,
    school: p.schoolName,
    programCode: p.programCode,
    estimatedMinimum: p.estimatedMinimum,
    prerequisites: p.prerequisites,
    experientialLearning: p.experientialLearning,
    province: p.province,
    category: p.category,
    eligibility: p.eligibility,
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 2 }}>
      {categoryFilter && (
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => {
          if (fromEdvisor === '1') {
            console.log('🔄 Programs: Navigating back to EdVisor');
            // If we came from EdVisor, navigate back to EdVisor
            navigate('/edvisor');
          } else {
            // Otherwise, navigate back to Program Categories
            navigate('/program-categories');
          }
        }}>
          &larr; Back {fromEdvisor === '1' ? 'to EdVisor' : 'to Program Categories'}
        </Button>
      )}
      <Typography variant="h4" sx={{ mb: 2 }}>
        {category ? `${category.name} Programs` : 'Programs'}
      </Typography>
      {categoryFilter && categoryDescription && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {categoryDescription}
        </Typography>
      )}
      <ProgramsList programs={programListItems} />
    </Box>
  );
};

export default Programs; 